import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { getUser } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Send, Search, ClipboardList, History as HistoryIcon, RotateCcw } from "lucide-react";

const TABS = [
  { key: "new", label: "New Request", icon: Plus },
  { key: "history", label: "History", icon: HistoryIcon },
];

// Default history window — last 3 months
function defaultRange() {
  const to = new Date();
  const from = new Date(); from.setMonth(from.getMonth() - 3);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

export default function POSStockRequests() {
  const u = getUser();
  const [tab, setTab] = useState("new");
  const [rows, setRows] = useState([]);

  // — New Request state —
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  // — History filters —
  const initial = useMemo(defaultRange, []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [status,   setStatus]   = useState("");   // "" = all

  const load = useCallback(() => {
    const params = {};
    if (status)   params.status     = status;
    if (dateFrom) params.start_date = dateFrom;
    if (dateTo)   params.end_date   = dateTo;
    api.get("/pos/stock-requests", { params }).then((r) => setRows(r.data || []));
  }, [status, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => api.get("/pos/products/search", { params: { q } }).then((r) => setResults(r.data || [])), 250);
    return () => clearTimeout(t);
  }, [q]);

  const add = (p) => {
    setLines((c) => [...c, { product_id: p.id, product_name: p.name, product_sku: p.sku || p.code, qty: 1, rate: 0 }]);
    setQ(""); setResults([]);
  };
  const set = (i, patch) => setLines((c) => c.map((l, ii) => i === ii ? { ...l, ...patch } : l));
  const remove = (i) => setLines((c) => c.filter((_, ii) => ii !== i));

  const submit = async () => {
    if (!lines.length) return toast.error("Add at least one line");
    try {
      await api.post("/pos/stock-requests", {
        store_id: u.location_id, store_type: u.department, notes,
        lines: lines.map((l) => ({ product_id: l.product_id, product_name: l.product_name, product_sku: l.product_sku, qty: Number(l.qty) || 0, rate: 0 })),
      });
      toast.success("Request sent to HQ");
      setLines([]); setNotes(""); setTab("history"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Server does the filtering — this is just a memo passthrough.
  const filteredRows = rows;

  const resetFilters = () => {
    const d = defaultRange();
    setDateFrom(d.from); setDateTo(d.to); setStatus("");
  };

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <Truck className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-semibold flex-1">Stock Request to HQ</h2>
          <div className="flex gap-1 h-8 rounded-md border border-slate-200 p-0.5 bg-slate-50">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sreq-tab-${t.key}`}
                  className={`px-3 rounded text-xs font-semibold inline-flex items-center gap-1 ${tab === t.key ? "bg-white text-red-700 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
                  <Icon className="h-3 w-3" />{t.label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "new" && (
          <div className="p-4 space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product to add…" className="pl-8" data-testid="sreq-search" />
            </div>
            {results.length > 0 && (
              <div className="border border-slate-100 rounded max-h-48 overflow-auto">
                {results.map((p) => <button key={p.id} onClick={() => add(p)} className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-sm"><b>{p.name}</b> · <span className="font-mono text-xs text-slate-500">{p.sku || p.code}</span></button>)}
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500"><th className="px-3 py-2">Product</th><th className="px-3 py-2 w-32 text-center">Qty</th><th className="px-3 py-2 w-10"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {lines.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400">Search to add lines.</td></tr>}
                {lines.map((l, i) => (
                  <tr key={i}><td className="px-3 py-2"><b>{l.product_name}</b><div className="text-[11px] font-mono text-slate-500">{l.product_sku}</div></td>
                    <td className="px-3 py-2 text-center"><input type="number" value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} className="h-7 w-20 text-center border border-slate-200 rounded text-sm tabular-nums" data-testid={`sreq-qty-${i}`} /></td>
                    <td className="px-3 py-2 text-center"><button onClick={() => remove(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></button></td></tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2">
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes to HQ" className="flex-1" />
              <Button onClick={submit} disabled={!lines.length} className="bg-red-600 hover:bg-red-700" data-testid="sreq-submit"><Send className="h-4 w-4 mr-1" />Send</Button>
            </div>
          </div>
        )}

        {tab === "history" && (
          <>
            <div className="p-3 border-b border-slate-100 flex items-center gap-2 flex-wrap bg-slate-50/50">
              <ClipboardList className="h-4 w-4 text-slate-400" />
              <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Filters</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40" data-testid="sreq-from" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40" data-testid="sreq-to" />
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2" data-testid="sreq-status">
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="dispatched">Dispatched</option>
                <option value="received">Received</option>
                <option value="rejected">Rejected</option>
              </select>
              <button onClick={resetFilters} className="text-[11px] text-slate-500 hover:text-slate-900 underline flex items-center gap-1" data-testid="sreq-reset">
                <RotateCcw className="h-3 w-3" /> Reset (last 3 mo.)
              </button>
              <span className="text-xs text-slate-500 ml-auto">{filteredRows.length} request(s)</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500"><th className="px-3 py-2">Req #</th><th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Lines</th><th className="px-3 py-2">Transfer</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No requests in this window.</td></tr>}
                {filteredRows.map((r) => (
                  <tr key={r.id} data-testid={`sreq-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-red-700">{r.req_no}</td>
                    <td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{(r.lines || []).length}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.transfer_no || "—"}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} rejectedReason={r.reject_reason} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status, rejectedReason }) {
  const map = {
    pending:    { tone: "bg-amber-50 text-amber-700 border-amber-200",       label: "🟡 Pending"       },
    approved:   { tone: "bg-blue-50 text-blue-700 border-blue-200",           label: "🔵 Approved"      },
    dispatched: { tone: "bg-indigo-50 text-indigo-700 border-indigo-200",     label: "🚚 Dispatched"    },
    received:   { tone: "bg-emerald-50 text-emerald-700 border-emerald-200",  label: "🟢 Received"      },
    rejected:   { tone: "bg-red-50 text-red-700 border-red-200",              label: "🔴 Rejected"      },
  };
  const s = map[status] || { tone: "bg-gray-100 text-gray-700 border-gray-200", label: status || "—" };
  return (
    <span className={`text-[11px] uppercase tracking-widest font-semibold border rounded px-1.5 py-0.5 ${s.tone}`}
      title={status === "rejected" && rejectedReason ? `Reason: ${rejectedReason}` : ""}>
      {s.label}
    </span>
  );
}
